import React, { useCallback, useEffect, useState } from "react";
import { Copy } from "lucide-react";
import { showGlobalToast } from "../utils/toast";
import "../styles/TableManagement.css";

interface PreviewUrlBoxProps {
  url: string;
  className?: string;
  showToast?: boolean;
}

/**
 * Reusable URL preview + copy component.
 * - Uses semantic classes defined in TableManagement.css so visual behavior
 *   (including dark mode) is controlled centrally.
 */
const PreviewUrlBox: React.FC<PreviewUrlBoxProps> = ({ url, className = "", showToast = false }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    if (!url) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      if (showToast) showGlobalToast("Copied link to clipboard","success");
    } catch (err) {
      console.error("Copy failed", err);
      if (showToast) showGlobalToast("Faild to copy link","success");
    }
  }, [url, showToast]);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1400);
    return () => clearTimeout(t);
  }, [copied]);

  return (
    <div className={`tm-modal-url ${className}`} role="group" aria-label="QR link">
      <div className="tm-modal-url-text" title={url} aria-label="QR url">
        {url}
      </div>
      <div className="tm-modal-url-actions">
        {copied && <span className="tm-modal-copied" aria-live="polite">Copied!</span>}
        <button
          type="button"
          className="tm-modal-copy-btn icon-btn"
          onClick={handleCopy}
          aria-label="Copy QR URL"
          title="Copy URL"
        >
          <Copy />
        </button>
      </div>
    </div>
  );
};

export default PreviewUrlBox;
